import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';

import UserSearchWidget from '~/components/Common/UserSearchWidget';
import { useRegionOptions } from '~/components/Tags/hooks/useRegionOptions';
import { buildSuggestedClonedVoucherName } from '~/components/Vouchers/voucherCloneUtils';
import { DEFAULT_VOUCHER_USER_EXPIRY_YEARS } from '~/components/Vouchers/voucherUserConstants';

import { useCloneVoucher } from '~/hooks/apis/promo/useCloneVoucher';
import { useCloneVoucherPreview } from '~/hooks/apis/promo/useCloneVoucherPreview';
import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { areCloneVoucherPreviewParamsEqual, voucherCloneQueryKeys } from '~/hooks/apis/promo/voucherCloneQueryKeys';

import type {
  CloneVoucherPreviewQuery,
  CloneVoucherRequestBody,
  CloneVoucherUserAssignment,
} from '~/services/PromoService';

import ApiError from '~/types/ApiError';

import { normalizeToISODate } from '~/utils/dateFormatter';
import { getCurrencyCodeByRegionCode } from '~/utils/getCurrencyCodeByRegionCode';

/** Voucher IDs from promo are UUIDs; used to choose sourceVoucherId vs sourceVoucherName for the clone API. */
const SOURCE_VOUCHER_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sourceQueryFromInput(
  sourceVoucherInput: string,
  fallbackSourceVoucherId: string,
): Pick<CloneVoucherPreviewQuery, 'sourceVoucherId' | 'sourceVoucherName'> {
  const raw = sourceVoucherInput.trim();
  if (!raw) {
    return { sourceVoucherId: fallbackSourceVoucherId };
  }
  if (SOURCE_VOUCHER_UUID_RE.test(raw)) {
    return { sourceVoucherId: raw };
  }
  return { sourceVoucherName: raw };
}

/**
 * Voucher UUID to attach users to (the clone source). Matches preview source when possible.
 * Returns null if the field contains another voucher’s name only (unresolved) while editable.
 */
function resolvedVoucherIdForAssignUsers(
  fallbackSourceVoucherId: string,
  sourceVoucherInput: string,
  isSourceLocked: boolean,
): string | null {
  const raw = sourceVoucherInput.trim();
  const fallback = fallbackSourceVoucherId.trim();
  if (SOURCE_VOUCHER_UUID_RE.test(raw)) {
    return raw;
  }
  if (!raw) {
    return fallback || null;
  }
  if (isSourceLocked) {
    return fallback || null;
  }
  return null;
}

type VoucherUserRow = {
  id: string;
  userId: string;
  /** Display string for UserSearchWidget after a selection (e.g. email). */
  userSearchLabel: string;
};

/** When the source voucher has a single reward, used to pre-fill regional clone amount after a region is chosen. */
export type VoucherCloneSourceReward = {
  reward_amount: number;
  currency_code?: string;
};

export type VoucherCloneDialogProps = {
  open: boolean;
  onClose: () => void;
  sourceVoucherId: string;
  /** Shown in helper text; optional name of the voucher being edited */
  sourceVoucherDisplayName?: string;
  /**
   * Current source voucher rewards (e.g. from the edit form). If exactly one reward exists,
   * selecting a region pre-fills reward amount when currencies align (or when the reward has no currency).
   */
  sourceVoucherRewards?: ReadonlyArray<VoucherCloneSourceReward>;
  /**
   * When true (default if `sourceVoucherId` is non-empty), source ID/name is pre-filled and not editable.
   * Set false to allow choosing another source voucher in the field.
   */
  lockSourceVoucher?: boolean;
  /** Pre-filled new name, e.g. `${currentName}-copy` */
  suggestedNewVoucherName?: string;
  /**
   * When this voucher was created by cloning, the source voucher id (`cloned_from_id` from the API).
   * If set, the dialog loads that voucher’s name and uses it to build the suggested new name (cleaner than
   * chaining suffixes from the current auto-generated voucher name).
   */
  clonedFromVoucherId?: string;
  onCloned: (newVoucherId: string) => void;
};

function newRow(): VoucherUserRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    userId: '',
    userSearchLabel: '',
  };
}

/**
 * Preview GET query: with a region, includes `amount` only when it is valid (required for regional preview).
 */
function buildClonePreviewQuery(
  newVoucherName: string,
  sourceVoucherInput: string,
  fallbackSourceVoucherId: string,
  region: string,
  amountStr: string,
): CloneVoucherPreviewQuery | null {
  const name = newVoucherName.trim();
  if (!name) {
    return null;
  }
  const base: CloneVoucherPreviewQuery = {
    newVoucherName: name,
    ...sourceQueryFromInput(sourceVoucherInput, fallbackSourceVoucherId),
  };
  const regionTrim = region.trim();
  if (!regionTrim) {
    return base;
  }
  const trimmedAmount = amountStr.trim();
  if (trimmedAmount === '') {
    return null;
  }
  const amount = Number(trimmedAmount);
  if (!Number.isFinite(amount) || amount < 0) {
    return null;
  }
  return { ...base, region: regionTrim, amount };
}

/** Strict base for clone POST — region requires a valid amount. */
function buildVoucherCloneBaseFields(
  newVoucherName: string,
  sourceVoucherInput: string,
  fallbackSourceVoucherId: string,
  region: string,
  amountStr: string,
): CloneVoucherPreviewQuery | null {
  const name = newVoucherName.trim();
  if (!name) {
    return null;
  }
  const base: CloneVoucherPreviewQuery = {
    newVoucherName: name,
    ...sourceQueryFromInput(sourceVoucherInput, fallbackSourceVoucherId),
  };
  const regionTrim = region.trim();
  if (regionTrim) {
    base.region = regionTrim;
    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount < 0) {
      return null;
    }
    base.amount = amount;
  }
  return base;
}

function buildCloneBody(
  newVoucherName: string,
  sourceVoucherInput: string,
  fallbackSourceVoucherId: string,
  region: string,
  amountStr: string,
  voucherUserRows: Array<VoucherUserRow>,
): CloneVoucherRequestBody | null {
  const base = buildVoucherCloneBaseFields(
    newVoucherName,
    sourceVoucherInput,
    fallbackSourceVoucherId,
    region,
    amountStr,
  );
  if (!base) {
    return null;
  }
  const body: CloneVoucherRequestBody = { ...base };

  const assignments: Array<CloneVoucherUserAssignment> = [];
  for (const row of voucherUserRows) {
    const userId = row.userId.trim();
    if (!userId) {
      continue;
    }
    const entry: CloneVoucherUserAssignment = { user_id: userId };
    assignments.push(entry);
  }

  if (assignments.length > 0) {
    body.voucherUsers = assignments;
  }

  return body;
}

function validationMessageForMissingBaseFields(regionFilled: boolean): string {
  return regionFilled
    ? 'Enter a valid reward amount (0 or greater) for the selected region.'
    : 'Enter a new voucher name.';
}

function formatClonePreviewApiErrorDetails(errors: unknown): string {
  if (errors == null || errors === '') {
    return '';
  }
  if (typeof errors === 'string') {
    return errors.trim();
  }
  if (Array.isArray(errors)) {
    return errors
      .map((item) => {
        if (typeof item === 'string') {
          return item.trim();
        }
        if (item && typeof item === 'object' && 'message' in item) {
          const message = (item as { message: unknown }).message;
          return typeof message === 'string' ? message.trim() : '';
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

/** Maps thrown `request()` / `ApiError` failures from the clone preview GET into copy for the dialog. */
function formatClonePreviewErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const headline = error.message?.trim() || 'Preview request failed';
    const statusNote = typeof error.status === 'number' ? ` (HTTP ${error.status})` : '';
    const details = formatClonePreviewApiErrorDetails(error.errors);
    return details ? `${headline}${statusNote}\n${details}` : `${headline}${statusNote}`;
  }
  if (error instanceof Error) {
    return error.message.trim() || 'Preview request failed';
  }
  return 'Preview request failed';
}

export default function VoucherCloneDialog({
  open,
  onClose,
  sourceVoucherId,
  sourceVoucherDisplayName,
  lockSourceVoucher: lockSourceVoucherProp,
  suggestedNewVoucherName = '',
  clonedFromVoucherId,
  sourceVoucherRewards,
  onCloned,
}: VoucherCloneDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const cloneMutation = useCloneVoucher();
  const assignUsersMutation = useCreateVoucherUsers();
  const { regionOptions } = useRegionOptions();

  const isSourceLocked = lockSourceVoucherProp ?? sourceVoucherId.trim().length > 0;

  const [newVoucherName, setNewVoucherName] = useState('');
  const [sourceVoucherInput, setSourceVoucherInput] = useState('');
  const [region, setRegion] = useState('');
  const [amount, setAmount] = useState('');
  const [voucherUserRows, setVoucherUserRows] = useState<Array<VoucherUserRow>>([]);
  const [moreInfo, setMoreInfo] = useState(false);
  const [previewParams, setPreviewParams] = useState<CloneVoucherPreviewQuery | null>(null);
  const wasOpenRef = useRef(false);
  /** Value of "New voucher name" last set by reset (or parent-name upgrade); used to avoid overwriting user edits. */
  const newNameBaselineRef = useRef('');

  const clonedFromIdTrimmed = clonedFromVoucherId?.trim() ?? '';
  const parentVoucherQuery = useGetVouchersByIds({
    voucherIds: clonedFromIdTrimmed ? [clonedFromIdTrimmed] : [],
    enabled: open && Boolean(clonedFromIdTrimmed),
  });
  const parentVoucherName = clonedFromIdTrimmed
    ? parentVoucherQuery.data?.get(clonedFromIdTrimmed)?.voucherName?.trim()
    : undefined;

  const sourceRewardCount = sourceVoucherRewards?.length ?? 0;
  const firstSourceRewardAmount = sourceVoucherRewards?.[0]?.reward_amount;
  const firstSourceRewardCurrency = sourceVoucherRewards?.[0]?.currency_code;

  const singleRewardKey = useMemo(() => {
    if (
      sourceRewardCount !== 1 ||
      firstSourceRewardAmount == null ||
      !Number.isFinite(Number(firstSourceRewardAmount))
    ) {
      return '';
    }
    return `${firstSourceRewardAmount}\0${firstSourceRewardCurrency ?? ''}`;
  }, [sourceRewardCount, firstSourceRewardAmount, firstSourceRewardCurrency]);

  /** Pre-fill amount when a region is selected and there is exactly one reward (currency-aware when possible). */
  useEffect(() => {
    if (!open || !region.trim()) {
      return;
    }
    const rCode = region.trim();
    if (!singleRewardKey) {
      return;
    }
    const [amountPart, currencyPart] = singleRewardKey.split('\0');
    const rewardAmount = Number(amountPart);
    if (!Number.isFinite(rewardAmount)) {
      return;
    }
    const regionCurrency = getCurrencyCodeByRegionCode(rCode);
    if (currencyPart && regionCurrency && currencyPart !== regionCurrency) {
      return;
    }
    setAmount(String(rewardAmount));
  }, [open, region, singleRewardKey]);

  useEffect(() => {
    if (!region.trim()) {
      setAmount('');
    }
  }, [region]);

  const resetForm = useCallback(() => {
    newNameBaselineRef.current = suggestedNewVoucherName;
    setNewVoucherName(suggestedNewVoucherName);
    if (isSourceLocked) {
      const display = sourceVoucherDisplayName?.trim();
      setSourceVoucherInput(display || sourceVoucherId);
    } else {
      setSourceVoucherInput('');
    }
    setRegion('');
    setAmount('');
    setVoucherUserRows([]);
    setMoreInfo(false);
    setPreviewParams(null);
  }, [isSourceLocked, sourceVoucherDisplayName, sourceVoucherId, suggestedNewVoucherName]);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened) {
      resetForm();
      queryClient.removeQueries({ queryKey: voucherCloneQueryKeys.previews() });
    }
  }, [open, queryClient, resetForm]);

  /** Prefer the original parent voucher’s name when this voucher is already a clone (avoids stacked random suffixes). */
  useEffect(() => {
    if (!open || !clonedFromIdTrimmed || !parentVoucherName) {
      return;
    }
    const fromParent = buildSuggestedClonedVoucherName(parentVoucherName);
    if (!fromParent) {
      return;
    }
    setNewVoucherName((prev) => {
      if (prev !== newNameBaselineRef.current) {
        return prev;
      }
      newNameBaselineRef.current = fromParent;
      return fromParent;
    });
  }, [open, clonedFromIdTrimmed, parentVoucherName]);

  const previewQuery = useCloneVoucherPreview({
    params: previewParams,
    enabled: open && Boolean(previewParams),
  });

  const renderRegionSelectValue = useCallback(
    (selected: string) => {
      const trimmed = selected.trim();
      if (!trimmed) {
        return (
          <Typography component="span" sx={{ fontStyle: 'italic' }}>
            Clone all regions
          </Typography>
        );
      }
      const opt = regionOptions.find((r) => r.code === trimmed);
      return opt ? `${opt.name} (${opt.code})` : trimmed;
    },
    [regionOptions],
  );

  const regionFilled = region.trim().length > 0;
  const hasUsersToAssignOnClone = useMemo(
    () => voucherUserRows.some((row) => row.userId.trim().length > 0),
    [voucherUserRows],
  );
  /** No single-region reward override — cloning copies all regions as-is. */
  const showAssignOnVoucherInsteadSuggestion = hasUsersToAssignOnClone && !regionFilled;
  const targetVoucherIdForAssign = useMemo(
    () => resolvedVoucherIdForAssignUsers(sourceVoucherId, sourceVoucherInput, isSourceLocked),
    [sourceVoucherId, sourceVoucherInput, isSourceLocked],
  );
  const assignedUserIdsForSourceVoucher = useMemo(
    () => voucherUserRows.map((row) => row.userId.trim()).filter(Boolean),
    [voucherUserRows],
  );
  const canAssignUsersToSourceVoucher =
    showAssignOnVoucherInsteadSuggestion &&
    Boolean(targetVoucherIdForAssign) &&
    assignedUserIdsForSourceVoucher.length > 0;
  const amountValid = !regionFilled || (amount.trim() !== '' && Number(amount) >= 0 && Number.isFinite(Number(amount)));
  const canTriggerPreview = newVoucherName.trim().length > 0;

  const currentPreviewQuery = buildClonePreviewQuery(
    newVoucherName,
    sourceVoucherInput,
    sourceVoucherId,
    region,
    amount,
  );
  const previewMatchesCurrent = Boolean(
    previewParams && currentPreviewQuery && areCloneVoucherPreviewParamsEqual(currentPreviewQuery, previewParams),
  );
  const previewCompleteForClone =
    !regionFilled || (amountValid && currentPreviewQuery !== null && currentPreviewQuery.amount !== undefined);
  const canCloneAfterPreview =
    canTriggerPreview &&
    previewCompleteForClone &&
    previewMatchesCurrent &&
    previewQuery.isSuccess &&
    Boolean(previewQuery.data?.result) &&
    Boolean(previewQuery.data?.result?.newVoucherNameAvailable);

  /** Drop stale preview as soon as form fields diverge (e.g. region change). */
  useEffect(() => {
    if (!open || !previewParams) {
      return;
    }
    const current = buildClonePreviewQuery(newVoucherName, sourceVoucherInput, sourceVoucherId, region, amount);
    if (current && areCloneVoucherPreviewParamsEqual(current, previewParams)) {
      return;
    }
    setPreviewParams(null);
  }, [newVoucherName, sourceVoucherInput, sourceVoucherId, region, amount, previewParams, open]);

  /** Re-run preview when name, source, region, or amount change. */
  useEffect(() => {
    if (!open) {
      return;
    }
    const q = buildClonePreviewQuery(newVoucherName, sourceVoucherInput, sourceVoucherId, region, amount);
    const id = window.setTimeout(() => {
      if (!q) {
        setPreviewParams(null);
        return;
      }
      setPreviewParams(q);
    }, 350);
    return () => window.clearTimeout(id);
  }, [open, newVoucherName, sourceVoucherInput, sourceVoucherId, region, amount]);

  const handlePreview = () => {
    const q = buildClonePreviewQuery(newVoucherName, sourceVoucherInput, sourceVoucherId, region, amount);
    if (!q) {
      if (!newVoucherName.trim()) {
        enqueueSnackbar('Enter a new voucher name.', { variant: 'warning' });
      } else if (region.trim()) {
        enqueueSnackbar(validationMessageForMissingBaseFields(true), { variant: 'warning' });
      } else {
        enqueueSnackbar('Unable to build preview request.', { variant: 'warning' });
      }
      return;
    }
    const samePreviewKey = Boolean(previewParams) && areCloneVoucherPreviewParamsEqual(q, previewParams);
    setPreviewParams(q);
    if (samePreviewKey) {
      void previewQuery.refetch();
    }
  };

  const previewResult = previewQuery.data?.result;
  const showPreviewSummary = Boolean(previewParams && previewQuery.isSuccess && previewResult);
  const previewErrorMessage = previewQuery.isError ? formatClonePreviewErrorMessage(previewQuery.error) : '';
  const previewErrorLines = previewErrorMessage
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const previewErrorHeadline = previewErrorLines[0] ?? '';
  const previewErrorDetailBlock = previewErrorLines.slice(1).join('\n').trim();

  const handleClone = async () => {
    if (!canCloneAfterPreview) {
      const result = previewQuery.data?.result;
      if (
        previewMatchesCurrent &&
        previewCompleteForClone &&
        previewQuery.isSuccess &&
        result &&
        !result.newVoucherNameAvailable
      ) {
        enqueueSnackbar('This voucher name is not available. Choose a different name before cloning.', {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar('Wait for a successful preview before cloning, or fix the errors shown above.', {
          variant: 'warning',
        });
      }
      return;
    }
    const body = buildCloneBody(newVoucherName, sourceVoucherInput, sourceVoucherId, region, amount, voucherUserRows);
    if (!body) {
      enqueueSnackbar(validationMessageForMissingBaseFields(regionFilled), { variant: 'warning' });
      return;
    }
    try {
      const res = await cloneMutation.mutateAsync(body);
      const newId = res?.result?.voucherId ?? '';
      if (!newId) {
        enqueueSnackbar('Clone succeeded but no voucher id was returned.', { variant: 'warning' });
        return;
      }
      enqueueSnackbar('Voucher cloned successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      onCloned(newId);
      onClose();
    } catch (e) {
      const message = formatClonePreviewErrorMessage(e);
      enqueueSnackbar(`Clone failed: ${message}`, { variant: 'error' });
    }
  };

  const addUserRow = () => setVoucherUserRows((rows) => [...rows, newRow()]);
  const removeUserRow = (id: string) => setVoucherUserRows((rows) => rows.filter((r) => r.id !== id));
  const updateUserRow = (id: string, patch: Partial<VoucherUserRow>) => {
    setVoucherUserRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleAssignUsersToSourceVoucher = useCallback(async () => {
    const vid = targetVoucherIdForAssign;
    if (!vid || assignedUserIdsForSourceVoucher.length === 0) {
      enqueueSnackbar('Select at least one user.', { variant: 'warning' });
      return;
    }
    try {
      const response = await assignUsersMutation.mutateAsync({
        voucher_id: vid,
        expired_at: normalizeToISODate(dayjs().add(DEFAULT_VOUCHER_USER_EXPIRY_YEARS, 'year').format('YYYY-MM-DD')),
        voucher_users: assignedUserIdsForSourceVoucher.map((user_id) => ({ user_id })),
      });
      const r = response.result;
      const created = r?.createdVoucherUsers?.length ?? 0;
      const updated = r?.updatedVoucherUsers?.length ?? 0;
      const reactivated = r?.reactivatedVoucherUsers?.length ?? 0;
      const skipped = r?.skippedVoucherUsers?.length ?? 0;
      const ok = created + updated + reactivated;
      if (ok === 0 && skipped > 0) {
        enqueueSnackbar('No users were assigned (all skipped).', { variant: 'warning' });
      } else if (skipped > 0) {
        enqueueSnackbar(`Assigned ${ok} user(s); ${skipped} skipped.`, { variant: 'warning' });
      } else if (ok === 0 && skipped === 0) {
        enqueueSnackbar('Users assigned to this voucher.', { variant: 'success' });
      } else if (ok === 1) {
        enqueueSnackbar('User assigned to this voucher.', { variant: 'success' });
      } else {
        enqueueSnackbar(`${ok} users assigned to this voucher.`, { variant: 'success' });
      }
      queryClient.invalidateQueries({ queryKey: ['voucher-users', vid] });
      queryClient.invalidateQueries({ queryKey: ['voucher', vid] });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      setVoucherUserRows([]);
      onClose();
    } catch (error) {
      enqueueSnackbar(`Failed to assign users: ${(error as Error).message}`, { variant: 'error' });
    }
  }, [
    assignUsersMutation,
    assignedUserIdsForSourceVoucher,
    enqueueSnackbar,
    onClose,
    queryClient,
    targetVoucherIdForAssign,
  ]);

  const sourceDisplayNameTrimmed = sourceVoucherDisplayName?.trim() ?? '';
  const launchedWithVoucherName = Boolean(sourceDisplayNameTrimmed);

  const sourceHint = useMemo(() => {
    const raw = sourceVoucherInput.trim();
    if (!raw) {
      if (launchedWithVoucherName) {
        return `Source is this voucher ("${sourceDisplayNameTrimmed}").`;
      }
      return `Source is this voucher (ID ${sourceVoucherId}).`;
    }
    if (SOURCE_VOUCHER_UUID_RE.test(raw)) {
      return `Source will be the voucher with ID ${raw}.`;
    }
    return `Source is "${raw}".`;
  }, [sourceVoucherInput, launchedWithVoucherName, sourceDisplayNameTrimmed, sourceVoucherId]);

  const dialogTitleId = 'voucher-clone-dialog-title';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={dialogTitleId}>
      <DialogTitle id={dialogTitleId} sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          Clone voucher
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title={moreInfo ? 'Hide detailed field help' : 'Show detailed field help'}>
            <ToggleButton
              value="info"
              size="small"
              selected={moreInfo}
              onChange={() => setMoreInfo((v) => !v)}
              aria-label={moreInfo ? 'Hide detailed field information' : 'Show detailed field information'}
            >
              <InfoOutlinedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <IconButton aria-label="Close" edge="end" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {moreInfo && (
            <Typography variant="body2" color="text.secondary">
              {sourceHint}
            </Typography>
          )}
          <TextField
            label={isSourceLocked ? 'Source voucher' : 'Source voucher ID or name (optional)'}
            value={sourceVoucherInput}
            onChange={(e) => setSourceVoucherInput(e.target.value)}
            fullWidth
            disabled={isSourceLocked}
            placeholder={
              isSourceLocked ? undefined : 'Leave empty for this voucher, or enter another voucher UUID or name'
            }
            helperText={
              moreInfo
                ? isSourceLocked
                  ? 'The rewards and conditions of this voucher will be cloned.'
                  : 'Empty = this voucher. Otherwise enter a UUID or another voucher’s name.'
                : undefined
            }
          />
          <TextField
            label="New voucher name"
            value={newVoucherName}
            onChange={(e) => setNewVoucherName(e.target.value)}
            required
            fullWidth
          />

          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Override discount amount (optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth>
                <InputLabel id="voucher-clone-region-label" shrink>
                  Region (optional)
                </InputLabel>
                <Select
                  labelId="voucher-clone-region-label"
                  label="Region (optional)"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  displayEmpty
                  renderValue={renderRegionSelectValue}
                >
                  <MenuItem value="">
                    <Typography component="span" sx={{ fontStyle: 'italic' }}>
                      Clone all regions
                    </Typography>
                  </MenuItem>
                  {regionOptions.map((r) => (
                    <MenuItem key={r.code} value={r.code}>
                      {r.name} ({r.code})
                    </MenuItem>
                  ))}
                </Select>
                {moreInfo && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {regionFilled
                      ? 'Enter a reward amount (≥ 0). Preview must succeed before Clone is enabled.'
                      : 'Leave as all regions, or pick one region for a single regional reward.'}
                  </Typography>
                )}
              </FormControl>
              {!regionFilled ? (
                <Typography variant="body2" color="text.secondary">
                  Select a region above to set a reward amount for a single regional reward.
                </Typography>
              ) : (
                <TextField
                  label="Reward amount (optional)"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  fullWidth
                  type="number"
                  required
                  inputProps={{ min: 0, step: 'any' }}
                  helperText={
                    moreInfo ? 'Required (≥ 0). Pre-filled when this voucher has one matching reward.' : undefined
                  }
                />
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Assign users on clone (optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Box>
                  <Button size="small" startIcon={<AddIcon />} onClick={addUserRow} variant="text">
                    Add user
                  </Button>
                </Box>
                {voucherUserRows.length === 0 ? (
                  moreInfo ? (
                    <Typography variant="body2" color="text.secondary">
                      Optional. Add rows to assign users on clone.
                    </Typography>
                  ) : null
                ) : (
                  <Stack spacing={2}>
                    {showAssignOnVoucherInsteadSuggestion && (
                      <Alert severity="info">
                        <Stack spacing={1.5}>
                          <Typography variant="body2">
                            You are assigning users while cloning the full voucher (all regions) without changing reward
                            amounts. If you only need to link users to this voucher, you can assign them to the source
                            voucher below, or cancel and use <strong>Add voucher users</strong> on the voucher
                            page—cloning always creates a new voucher with a new name.
                          </Typography>
                          {targetVoucherIdForAssign ? (
                            <Box>
                              <LoadingButton
                                variant="outlined"
                                color="primary"
                                size="small"
                                loading={assignUsersMutation.isPending}
                                disabled={!canAssignUsersToSourceVoucher || cloneMutation.isPending}
                                onClick={handleAssignUsersToSourceVoucher}
                              >
                                {assignedUserIdsForSourceVoucher.length === 1
                                  ? 'Assign user to this voucher'
                                  : `Assign ${assignedUserIdsForSourceVoucher.length} users to this voucher`}
                              </LoadingButton>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {`Uses the same default expiry as Add voucher users (${DEFAULT_VOUCHER_USER_EXPIRY_YEARS} ${
                                  DEFAULT_VOUCHER_USER_EXPIRY_YEARS === 1 ? 'year' : 'years'
                                } from today).`}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Enter the source voucher as a UUID in the source field, or go back to this voucher’s edit
                              page to assign users—the source voucher must be known to assign from here.
                            </Typography>
                          )}
                        </Stack>
                      </Alert>
                    )}
                    {voucherUserRows.map((row) => (
                      <Stack
                        key={row.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ sm: 'flex-start' }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <UserSearchWidget
                            id={`voucher-clone-user-${row.id}`}
                            label="Name / Email / Customer Support Code / User ID"
                            formErrors={[]}
                            onChange={(_, value) =>
                              updateUserRow(row.id, {
                                userId: value,
                                ...(value === '' ? { userSearchLabel: '' } : {}),
                              })
                            }
                            passedAutocompleteValue={row.userSearchLabel}
                            setPassedAutocompleteValue={(next) => {
                              setVoucherUserRows((rows) =>
                                rows.map((r) => {
                                  if (r.id !== row.id) {
                                    return r;
                                  }
                                  const prev = r.userSearchLabel;
                                  const label = typeof next === 'function' ? next(prev) : next;
                                  return { ...r, userSearchLabel: label };
                                }),
                              );
                            }}
                          />
                        </Box>
                        <IconButton aria-label="Remove user row" onClick={() => removeUserRow(row.id)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            {previewQuery.isFetching && (
              <Typography variant="caption" color="text.secondary">
                Loading preview…
              </Typography>
            )}
            {canTriggerPreview && !canCloneAfterPreview && !previewQuery.isFetching && (
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: '100%' }}>
                {previewQuery.isError ? (
                  <>
                    <Box component="span" sx={{ color: 'error.main', display: 'block', fontWeight: 500 }}>
                      {previewErrorHeadline}
                    </Box>
                    <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                      Fix preview errors to enable Clone.
                    </Box>
                  </>
                ) : regionFilled && !amountValid ? (
                  'Enter a reward amount (0 or greater) to finish preview.'
                ) : previewQuery.isSuccess &&
                  previewQuery.data?.result &&
                  !previewQuery.data.result.newVoucherNameAvailable &&
                  previewMatchesCurrent &&
                  previewCompleteForClone ? (
                  'This voucher name is not available. Choose a different name before cloning.'
                ) : (
                  'Waiting for preview…'
                )}
              </Typography>
            )}
          </Stack>

          {previewQuery.isError && previewErrorDetailBlock && (
            <Alert severity="error">
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                {previewErrorDetailBlock}
              </Typography>
            </Alert>
          )}

          {showPreviewSummary && previewResult && (
            <Alert severity={previewResult.newVoucherNameAvailable ? 'success' : 'warning'}>
              <Typography variant="body2">
                Mode: {previewResult.cloneMode} · Rewards: {previewResult.rewardCount} · Conditions:{' '}
                {previewResult.conditionCount}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                New name available: {previewResult.newVoucherNameAvailable ? 'yes' : 'no'}
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handlePreview} disabled={!canTriggerPreview} aria-label="Refresh preview">
          <RefreshIcon />
        </Button>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={cloneMutation.isPending}
          onClick={handleClone}
          disabled={!canCloneAfterPreview}
        >
          Clone
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
